import React, { useEffect } from 'react';
import { RuleSettingItem } from './RuleSettingItem';
import { useFormik } from 'formik';
import { Box, Divider } from '@mui/material';
import { Button } from 'system/atoms';
import AddIcon from '@mui/icons-material/Add';
import {
  CandidateStatus,
  RequirementConditionType,
  AutomationActionType,
  RequirementConditionOperator
} from 'generated-graphql/types';
import { getAvailableActions } from 'modules/analyse/utils/getAvailableActions';
import { AutomationFormItem } from 'modules/analyse/types/AutomationFormItem';
import { getDefaultRequirementCondition } from 'modules/analyse/utils/getDefaultRequirementCondition';
import { AlertCard } from 'common/ui';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';
import { checkAnalysisHasRequirementsNotMet } from 'modules/analyse-automations/utils/checkAnalysisHasRequirementsNotMet';

type ConversationRulePriorityAlertProps = {
  automationTasks: AutomationFormItem[];
};

const ConversationRulePriorityAlert: React.FC<
  ConversationRulePriorityAlertProps
> = ({ automationTasks }) => {
  const hasConversationScore = automationTasks.some((task) =>
    task.requirementConditions.some(
      (condition) =>
        condition.type === RequirementConditionType.ConversationScore
    )
  );

  const hasCandidateScore = automationTasks.some((task) =>
    task.requirementConditions.some(
      (condition) =>
        condition.type === RequirementConditionType.CandidateScoreCondition
    )
  );

  if (!hasConversationScore || !hasCandidateScore) {
    return null;
  }

  return (
    <AlertCard
      type="info"
      title="Conversation rules take priority if there is a conflict"
      sx={{ mt: '40px' }}
    />
  );
};

type Props = {
  onFormChange: (values: AutomationFormItem[]) => void;
  analysisAutomationItems: AutomationFormItem[];
  candidateBatchId?: string;
};

export const RulesSettings: React.FC<Props> = ({
  analysisAutomationItems,
  onFormChange,
  candidateBatchId
}) => {
  const { track } = useTracking();

  const formik = useFormik<AutomationFormItem[]>({
    initialValues: analysisAutomationItems || [],
    onSubmit: onFormChange,
    enableReinitialize: true
  });

  useEffect(() => {
    onFormChange(formik.values);
  }, [formik.values]);

  const {
    candidateStatusesAvailableOptions: actionCandidateStatusAvailableOptions
  } = getAvailableActions(formik.values);

  const addAutomation = (
    defaultRequirementConditionScoreType: RequirementConditionType
  ) => {
    if (
      defaultRequirementConditionScoreType ===
      RequirementConditionType.ConversationScore
    ) {
      if (candidateBatchId) {
        track(TrackingEventName.AnalysisEditRulesConversationRuleClick, {
          analysisId: candidateBatchId
        });
      } else {
        track(TrackingEventName.AnalysisCreateConversationRuleClick);
      }
    }

    const defaultCandidateStatus = CandidateStatus.ShortListed;

    const defaultCandidateStatusAction =
      actionCandidateStatusAvailableOptions.includes(defaultCandidateStatus)
        ? defaultCandidateStatus
        : actionCandidateStatusAvailableOptions[0];

    const newRules = [...analysisAutomationItems];

    if (
      defaultRequirementConditionScoreType ===
      RequirementConditionType.CandidateRequirementsMetCondition
    ) {
      const newRule: AutomationFormItem = {
        requirementConditions: [
          {
            type: RequirementConditionType.CandidateRequirementsMetCondition,
            operator: RequirementConditionOperator.IsFalse
          }
        ],
        action: {
          type: AutomationActionType.UpdateCandidateStatus,
          candidateStatus: defaultCandidateStatusAction,
          campaignId: undefined
        }
      };
      newRules.push(newRule);
    } else {
      const newRule: AutomationFormItem = {
        requirementConditions: getDefaultRequirementCondition(
          defaultRequirementConditionScoreType
        ),
        action: {
          type: AutomationActionType.UpdateCandidateStatus,
          candidateStatus: defaultCandidateStatusAction,
          campaignId: undefined
        }
      };
      newRules.push(newRule);
    }

    formik.setValues(newRules);
  };

  const removeAutomationItem = (index: number) => {
    const newValues = formik.values.filter((_, i) => i !== index);
    formik.setValues(newValues);
  };

  return (
    <>
      {analysisAutomationItems.map((analysisAutomationItem, index) => (
        <Box key={`rule-${index}`} sx={{ mb: '15px' }}>
          <Box my="30px">
            <RuleSettingItem
              index={index}
              analysisAutomationItem={analysisAutomationItem}
              setFieldValue={formik.setFieldValue}
              removeRuleItem={() => removeAutomationItem(index)}
            />
          </Box>
          <Divider key={`divider-${analysisAutomationItem.id}`} />
        </Box>
      ))}
      <Box
        display="flex"
        flexDirection="row"
        alignSelf="start"
        mb="15px"
        mt="15px"
      >
        <Button
          variant="tertiary"
          startIcon={<AddIcon />}
          onClick={() =>
            addAutomation(RequirementConditionType.CandidateScoreCondition)
          }
        >
          Add rule
        </Button>
        <Button
          variant="tertiary"
          startIcon={<AddIcon />}
          onClick={() =>
            addAutomation(RequirementConditionType.ConversationScore)
          }
        >
          Add conversation rule
        </Button>
        {/* You can only have one knockout rule */}
        {!checkAnalysisHasRequirementsNotMet(analysisAutomationItems) && (
          <Button
            variant="tertiary"
            startIcon={<AddIcon />}
            onClick={() =>
              addAutomation(
                RequirementConditionType.CandidateRequirementsMetCondition
              )
            }
          >
            Add knockout rule
          </Button>
        )}
      </Box>
      <Divider />
      <ConversationRulePriorityAlert
        automationTasks={analysisAutomationItems}
      />
    </>
  );
};
